import { create } from "zustand";

import type { Achievement } from "../features/achievements";
import type { GameReport } from "../utils/gameReportUtils";

// Define the modal state structure
export interface ModalState {
  // Basic Modal visibility states
  quickstartModalVisible: boolean;
  contactModalVisible: boolean;
  statsModalVisible: boolean;
  dailiesModalVisible: boolean;

  // Game Report Modal state
  gameReportModalVisible: boolean;
  gameReportModalReport: GameReport | null;
  /** A win report waiting for the board's win wave to finish. */
  heldGameReport: GameReport | null;

  // Definition Dialog State
  definitionDialogWord: string | null;
  definitionDialogPathIndex: number | null; // To pass path index to dialog for context
  definitionDialogVisible: boolean;

  // Achievement Detail Dialog State
  selectedAchievement: Achievement | null;
  achievementDialogVisible: boolean;

  // Actions
  // Basic Modal visibility actions
  setQuickstartModalVisible: (visible: boolean) => void;
  setContactModalVisible: (visible: boolean) => void;
  setStatsModalVisible: (visible: boolean) => void;
  setDailiesModalVisible: (visible: boolean) => void;

  // Game Report Modal actions
  showGameReportModal: (report: GameReport) => void;
  hideGameReportModal: () => void;
  holdGameReport: (report: GameReport) => void;
  /** Opens the held report (only the given one, when an id is passed). */
  releaseHeldGameReport: (reportId?: string) => void;
  /** Drops the held report without opening it. */
  discardHeldGameReport: () => void;

  // Definition Dialog Actions
  showWordDefinition: (word: string, pathIndex?: number | null) => void;
  hideWordDefinition: () => void;
  setDefinitionDialogWord: (word: string | null) => void;
  setDefinitionDialogPathIndex: (index: number | null) => void;
  setDefinitionDialogVisible: (visible: boolean) => void;

  // Achievement Detail Dialog Actions
  showAchievementDetail: (achievement: Achievement) => void;
  hideAchievementDetail: () => void;
  setSelectedAchievement: (achievement: Achievement | null) => void;
  setAchievementDialogVisible: (visible: boolean) => void;

  // Reset function to restore the store to its initial state
  reset: () => void;
}

// Create the Zustand store
export const useModalStore = create<ModalState>((set, _get) => ({
  // Initial state
  // Basic Modal visibility states
  quickstartModalVisible: false,
  contactModalVisible: false,
  statsModalVisible: false,
  dailiesModalVisible: false,

  // Game Report Modal state
  gameReportModalVisible: false,
  gameReportModalReport: null,
  heldGameReport: null,

  // Definition Dialog State
  definitionDialogWord: null,
  definitionDialogPathIndex: null,
  definitionDialogVisible: false,

  // Achievement Detail Dialog State
  selectedAchievement: null,
  achievementDialogVisible: false,

  // Actions
  // Basic Modal visibility actions
  setQuickstartModalVisible: (visible) =>
    set({ quickstartModalVisible: visible }),
  setContactModalVisible: (visible) => set({ contactModalVisible: visible }),
  setStatsModalVisible: (visible) => set({ statsModalVisible: visible }),
  setDailiesModalVisible: (visible) => set({ dailiesModalVisible: visible }),

  // Game Report Modal actions
  showGameReportModal: (report) =>
    set({
      gameReportModalVisible: true,
      gameReportModalReport: report,
    }),
  hideGameReportModal: () =>
    set({
      gameReportModalVisible: false,
      gameReportModalReport: null,
    }),
  holdGameReport: (report) => set({ heldGameReport: report }),
  releaseHeldGameReport: (reportId) => {
    const held = _get().heldGameReport;
    if (!held || (reportId !== undefined && held.id !== reportId)) return;
    set({
      heldGameReport: null,
      gameReportModalVisible: true,
      gameReportModalReport: held,
    });
  },
  discardHeldGameReport: () => set({ heldGameReport: null }),

  // Definition Dialog Actions
  showWordDefinition: (word: string, pathIndex?: number | null) => {
    set({
      definitionDialogWord: word,
      definitionDialogPathIndex: pathIndex ?? null,
      definitionDialogVisible: true,
    });
  },
  hideWordDefinition: () => {
    set({
      definitionDialogWord: null,
      definitionDialogPathIndex: null,
      definitionDialogVisible: false,
    });
  },
  setDefinitionDialogWord: (word) => set({ definitionDialogWord: word }),
  setDefinitionDialogPathIndex: (index) =>
    set({ definitionDialogPathIndex: index }),
  setDefinitionDialogVisible: (visible) =>
    set({ definitionDialogVisible: visible }),

  // Achievement Detail Dialog Actions
  showAchievementDetail: (achievement: Achievement) => {
    set({
      selectedAchievement: achievement,
      achievementDialogVisible: true,
    });
  },
  hideAchievementDetail: () => {
    set({
      selectedAchievement: null,
      achievementDialogVisible: false,
    });
  },
  setSelectedAchievement: (achievement) =>
    set({ selectedAchievement: achievement }),
  setAchievementDialogVisible: (visible) =>
    set({ achievementDialogVisible: visible }),

  // Reset function implementation - close all modals and dialogs
  reset: () => {
    console.log("🔄 Resetting useModalStore - closing all modals and dialogs");
    set({
      // Close all basic modals
      quickstartModalVisible: false,
      contactModalVisible: false,
      statsModalVisible: false,
      dailiesModalVisible: false,

      // Close game report modal
      gameReportModalVisible: false,
      gameReportModalReport: null,
      heldGameReport: null,

      // Close definition dialog
      definitionDialogWord: null,
      definitionDialogPathIndex: null,
      definitionDialogVisible: false,

      // Close achievement dialog
      selectedAchievement: null,
      achievementDialogVisible: false,
    });
  },
}));
